import { ui_service_ip } from "./ip_details";
import { call_get_api, call_delete_api, call_update_api, call_post_api } from "./event_handling";

function toPriority(priority, fallback = "low") {
    const value = String(priority || "").trim().toLowerCase();
    if (value === "critical" || value === "high" || value === "medium" || value === "low") {
        return value;
    }
    return fallback;
}

function mapCatalogueEntity(item = {}, extra = {}) {
    return {
        id: item.id,
        name: item.name || "",
        description: item.description || item.definition || item.defination || "",
        active: item.active !== false,
        priority: item.priority ? toPriority(item.priority, "") : "",
        ...extra
    };
}

/**
 * GET configure/business/catalogue — KPI Catalog hierarchy
 */
export function getConfigureBusinessCatalogue(requestParams = {}) {
    return call_get_api(ui_service_ip, "configure/business/catalogue", requestParams);
}

/**
 * POST configure/business/catalogue — Quick add business + functions + KPIs
 * Body: { business_name, function: [{ name, priority, kpi: [...] }] }
 */
export function createConfigureBusinessCatalogue(payload = {}) {
    return call_post_api(ui_service_ip, "configure/business/catalogue", {
        tenant_id: localStorage.getItem("team_id"),
        user_id: localStorage.getItem("user_id"),
        ...payload
    });
}

/**
 * PUT configure/business/catalogue — update an existing catalogue entity
 */
export function updateConfigureBusinessCatalogue(payload = {}) {
    return call_update_api(ui_service_ip, "configure/business/catalogue", {
        tenant_id: localStorage.getItem("team_id"),
        user_id: localStorage.getItem("user_id"),
        ...payload
    });
}

export function buildUpdateKpiCataloguePayload(kpi = {}) {
    return {
        kpi: {
            id: kpi.id,
            name: String(kpi.name || "").trim(),
            definition: String(kpi.description ?? kpi.definition ?? "").trim(),
            priority: toPriority(kpi.priority, "low")
        }
    };
}

export function isConfigureCatalogueUpdateSuccess(apiResponse) {
    return isConfigureCatalogueDeleteSuccess(apiResponse);
}

function mapCatalogueKpiItems(kpis = []) {
    return kpis
        .map((k) => {
            const kName = String(k.name || "").trim();
            if (!kName) return null;
            const item = {
                name: kName,
                description: String(k.description || "").trim(),
                priority: toPriority(k.priority, "low")
            };
            if (k.enable_computation !== undefined) {
                item.enable_computation = k.enable_computation === 0 ? 0 : 1;
            }
            return item;
        })
        .filter(Boolean);
}

function mapCatalogueFunctionItems(functions = []) {
    return functions
        .map((fn) => {
            const name = String(fn.name || "").trim();
            if (!name) return null;
            const kpis = mapCatalogueKpiItems(fn.kpis || []);
            return {
                name,
                priority: toPriority(fn.priority, "low"),
                kpi: kpis
            };
        })
        .filter(Boolean);
}

/** Quick add — new business area with nested functions + KPIs */
export function buildQuickAddCataloguePayload(formValues = {}) {
    return {
        business: {
            name: String(formValues.business_name || "").trim(),
            description: String(formValues.description || "").trim(),
            priority: toPriority(formValues.priority, "low")
        },
        function: mapCatalogueFunctionItems(formValues.functions || [])
    };
}

/** Add new business only */
export function buildAddBusinessCataloguePayload({ name, description = "", priority = "low" } = {}) {
    return {
        business: {
            name: String(name || "").trim(),
            description: String(description || "").trim(),
            priority: toPriority(priority, "low")
        },
        function: []
    };
}

/** Add new function under an existing business */
export function buildAddFunctionCataloguePayload(businessName, fn = {}, kpis = []) {
    const name = String(fn.name || "").trim();
    return {
        business_name: String(businessName || "").trim(),
        function: [{
            name,
            priority: toPriority(fn.priority, "low"),
            kpi: mapCatalogueKpiItems(kpis)
        }]
    };
}

/** Add new KPI under an existing business + function */
export function buildAddKpiCataloguePayload(businessName, functionName, kpi = {}) {
    return {
        business_name: String(businessName || "").trim(),
        function_name: String(functionName || "").trim(),
        kpi: {
            name: String(kpi.name || "").trim(),
            description: String(kpi.description || "").trim(),
            priority: toPriority(kpi.priority, "low")
        }
    };
}

export function isConfigureCatalogueCreateSuccess(apiResponse) {
    return isConfigureCatalogueDeleteSuccess(apiResponse);
}

/**
 * DELETE configure/business/catalogue
 * Body: { businessIds: [], functionIds: [], kpiIds: [] }
 */
export function deleteConfigureBusinessCatalogue(payload = {}) {
    return call_delete_api(ui_service_ip, "configure/business/catalogue", {
        tenant_id: localStorage.getItem("team_id"),
        user_id: localStorage.getItem("user_id"),
        businessIds: Array.isArray(payload.businessIds) ? payload.businessIds : [],
        functionIds: Array.isArray(payload.functionIds) ? payload.functionIds : [],
        kpiIds: Array.isArray(payload.kpiIds) ? payload.kpiIds : []
    });
}

export function toNumericIds(ids = []) {
    const values = ids instanceof Set ? [...ids] : (Array.isArray(ids) ? ids : []);
    return values
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id));
}

export function buildCatalogueDeletePayload(idsByType = {}) {
    return {
        businessIds: toNumericIds(idsByType.business),
        functionIds: toNumericIds(idsByType.function),
        kpiIds: toNumericIds(idsByType.kpi)
    };
}

export function isConfigureCatalogueDeleteSuccess(apiResponse) {
    if (!apiResponse || typeof apiResponse !== "object") {
        return false;
    }
    return Number(apiResponse.code) === 200 || Number(apiResponse.status_code) === 200;
}

/**
 * PUT configure/business/catalogue/enable
 * Body: { businessIds: [], functionIds: [], kpiIds: [], enable: true }
 */
export function updateConfigureBusinessCatalogueEnable(payload = {}) {
    return call_update_api(ui_service_ip, "configure/business/catalogue/enable", {
        tenant_id: localStorage.getItem("team_id"),
        user_id: localStorage.getItem("user_id"),
        businessIds: Array.isArray(payload.businessIds) ? payload.businessIds : [],
        functionIds: Array.isArray(payload.functionIds) ? payload.functionIds : [],
        kpiIds: Array.isArray(payload.kpiIds) ? payload.kpiIds : [],
        enable: Boolean(payload.enable)
    });
}

export function buildCatalogueEnablePayload(idsByType = {}, enable = true) {
    return {
        ...buildCatalogueDeletePayload(idsByType),
        enable: Boolean(enable)
    };
}

export function isConfigureCatalogueEnableSuccess(apiResponse) {
    return isConfigureCatalogueDeleteSuccess(apiResponse);
}

/**
 * Exact API shape:
 * { code: 200, message, data: { business|businesses, functions, kpis, models } }
 */
export function extractConfigureCatalogueData(apiResponse) {
    const empty = { business: [], functions: [], kpis: [], models: [] };
    if (!apiResponse || typeof apiResponse !== "object") {
        return empty;
    }

    let payload = apiResponse.data;
    if (!payload || typeof payload !== "object") {
        payload = apiResponse.response;
    }
    if (Array.isArray(payload)) {
        payload = payload[0];
    }
    if (!payload || typeof payload !== "object") {
        if (Array.isArray(apiResponse.business) || Array.isArray(apiResponse.businesses)) {
            payload = apiResponse;
        } else {
            return empty;
        }
    }

    return {
        business: Array.isArray(payload.business)
            ? payload.business
            : (Array.isArray(payload.businesses) ? payload.businesses : []),
        functions: Array.isArray(payload.functions) ? payload.functions : [],
        kpis: Array.isArray(payload.kpis) ? payload.kpis : [],
        models: Array.isArray(payload.models) ? payload.models : []
    };
}

export function normalizeConfigureCatalogueResponse(apiResponse) {
    const raw = extractConfigureCatalogueData(apiResponse);

    return {
        businesses: raw.business.map((item) => mapCatalogueEntity(item)),
        functions: raw.functions.map((item) =>
            mapCatalogueEntity(item, {
                businessId: item.businessId ?? item.business_id
            })
        ),
        kpis: raw.kpis.map((item) =>
            mapCatalogueEntity(item, {
                functionId: item.functionId ?? item.function_id,
                formula: item.formula || ""
            })
        ),
        models: raw.models.map((item) =>
            mapCatalogueEntity(item, {
                functionId: item.functionId ?? item.function_id,
                kind: item.kind || ""
            })
        )
    };
}

export function hasCatalogueBusinesses(apiResponse) {
    return normalizeConfigureCatalogueResponse(apiResponse).businesses.length > 0;
}

export function isConfigureCatalogueSuccess(apiResponse) {
    if (!apiResponse || typeof apiResponse !== "object") {
        return false;
    }
    if (Number(apiResponse.code) === 200 || Number(apiResponse.status_code) === 200) {
        return true;
    }
    return hasCatalogueBusinesses(apiResponse);
}

/**
 * GET configure/business/mappings/page — Data Assets Mapping page payload
 */
export function getConfigureBusinessMappingsPage(requestParams = {}) {
    return call_get_api(ui_service_ip, "configure/business/mappings/page", requestParams);
}

/**
 * GET business/mapping/lineage — Diagnostics map for a KPI mapping
 * Params: env_id, kpi_store_id
 */
export function getBusinessMappingLineage(requestParams = {}) {
    return call_get_api(ui_service_ip, "business/mapping/lineage", requestParams);
}

/**
 * POST configure/business/mappings — add mapping(s)
 */
export function createConfigureBusinessMapping(payload = {}) {
    return call_post_api(ui_service_ip, "configure/business/mappings", payload);
}

/**
 * PUT configure/business/mappings — update mapping(s)
 */
export function updateConfigureBusinessMapping(payload = {}) {
    return call_update_api(ui_service_ip, "configure/business/mappings", payload);
}

function mapApiMappingItems(mappings = []) {
    return mappings
        .map((item) => ({
            kpiId: Number(item.kpiId),
            assetId: Number(item.assetId),
            status: String(item.status || "manual").toLowerCase(),
            enabled: item.enabled !== false
        }))
        .filter((item) => Number.isFinite(item.kpiId) && Number.isFinite(item.assetId));
}

function resolveFormulaUpdate(kpi, formulaText) {
    const formula = String(formulaText || "").trim();
    if (!formula || !kpi) return null;
    const formulaId = kpi.kpi_store_id ?? kpi.id;
    if (formulaId == null) return null;
    return {
        id: Number(formulaId),
        formula
    };
}

function resolvePrimaryMappingAsset(modalPayload = {}, mappings = [], assets = []) {
    const source = Array.isArray(modalPayload.mappings) && modalPayload.mappings.length
        ? modalPayload.mappings
        : mappings;
    const selectedAssets = source
        .map((m) => assets.find((a) => String(a.id) === String(m.assetId)) || m.asset)
        .filter(Boolean);
    return selectedAssets.find((a) => a.datasource === modalPayload.datasource) || selectedAssets[0] || null;
}

function applyMappingAssetContext(body, primaryAsset, modalPayload = {}) {
    if (primaryAsset?.env_id != null) {
        body.env_id = Number(primaryAsset.env_id);
    } else if (modalPayload.envId != null && modalPayload.envId !== '' && Number.isFinite(Number(modalPayload.envId))) {
        body.env_id = Number(modalPayload.envId);
    }
    if (primaryAsset?.datasource_id != null) {
        body.datasource_id = Number(primaryAsset.datasource_id);
    }
    return body;
}

/** POST payload for Add mapping */
export function buildCreateMappingApiPayload(modalPayload = {}, context = {}) {
    const { kpis = [], assets = [] } = context;
    const kpi = kpis.find((item) => String(item.id) === String(modalPayload.kpiId));
    const mappings = mapApiMappingItems(modalPayload.mappings || []);
    const formulaUpdate = resolveFormulaUpdate(
        kpi,
        modalPayload.formulaUpdate?.formula ?? modalPayload.formula
    );
    const primaryAsset = resolvePrimaryMappingAsset(modalPayload, mappings, assets);

    const body = {
        tenant_id: Number(localStorage.getItem("team_id")),
        kpiId: Number(modalPayload.kpiId),
        businessId: Number(modalPayload.businessId),
        functionId: Number(modalPayload.functionId),
        datasource: String(modalPayload.datasource || "").trim(),
        replaceKpiId: null,
        mappings
    };

    applyMappingAssetContext(body, primaryAsset, modalPayload);

    if (formulaUpdate) {
        body.formulaUpdate = formulaUpdate;
    }

    return body;
}

/** PUT payload for Edit mapping */
export function buildUpdateMappingApiPayload(modalPayload = {}, context = {}) {
    const { kpis = [], assets = [] } = context;
    const kpi = kpis.find((item) => String(item.id) === String(modalPayload.kpiId));
    const mappings = mapApiMappingItems(modalPayload.mappings || []);
    const formulaUpdate = resolveFormulaUpdate(
        kpi,
        modalPayload.formulaUpdate?.formula ?? modalPayload.formula
    );

    const primaryAsset = resolvePrimaryMappingAsset(modalPayload, mappings, assets);

    const body = {
        tenant_id: String(localStorage.getItem("team_id") || ""),
        kpiId: Number(modalPayload.kpiId),
        replaceKpiId: Number(modalPayload.replaceKpiId ?? modalPayload.kpiId),
        datasource: String(modalPayload.datasource || "").trim(),
        mappings
    };

    applyMappingAssetContext(body, primaryAsset, modalPayload);
    if (formulaUpdate) {
        body.formulaUpdate = formulaUpdate;
    }

    return body;
}

export function isConfigureMappingMutationSuccess(apiResponse) {
    return isConfigureCatalogueDeleteSuccess(apiResponse);
}

/**
 * Extract assets/mappings (and optional catalogue slices) from mappings/page response.
 * Exact shape:
 * { code: 200, data: { assets, businesses, functions, kpis, models, mappings, metrics, filter_options }, message }
 */
export function extractConfigureMappingsPageData(apiResponse) {
    const empty = {
        assets: [],
        mappings: [],
        business: [],
        functions: [],
        kpis: [],
        models: [],
        metrics: null,
        filter_options: null,
        filters: null
    };
    if (!apiResponse || typeof apiResponse !== "object") {
        return empty;
    }

    let payload = apiResponse.data;
    if (!payload || typeof payload !== "object") {
        payload = apiResponse.response;
    }
    if (Array.isArray(payload)) {
        payload = payload[0];
    }
    if (!payload || typeof payload !== "object") {
        if (
            Array.isArray(apiResponse.assets)
            || Array.isArray(apiResponse.mappings)
            || Array.isArray(apiResponse.businesses)
            || Array.isArray(apiResponse.kpis)
        ) {
            payload = apiResponse;
        } else {
            return empty;
        }
    }

    const assets = Array.isArray(payload.assets)
        ? payload.assets
        : (Array.isArray(payload.asset) ? payload.asset : []);
    const mappings = Array.isArray(payload.mappings)
        ? payload.mappings
        : (Array.isArray(payload.mapping) ? payload.mapping : []);

    return {
        assets,
        mappings,
        business: Array.isArray(payload.business)
            ? payload.business
            : (Array.isArray(payload.businesses) ? payload.businesses : []),
        functions: Array.isArray(payload.functions) ? payload.functions : [],
        kpis: Array.isArray(payload.kpis) ? payload.kpis : [],
        models: Array.isArray(payload.models) ? payload.models : [],
        metrics: payload.metrics && typeof payload.metrics === "object" ? payload.metrics : null,
        filter_options: payload.filter_options || payload.filterOptions || null,
        filters: payload.filters || null
    };
}

function mapMappingsPageAsset(item = {}) {
    return {
        id: item.id ?? item.asset_id,
        datasource: item.datasource || item.data_source || item.datasource_name || "",
        datasource_id: item.datasource_id ?? item.datasourceId ?? null,
        dataset: item.dataset || item.dataset_name || item.table || "",
        attribute: item.attribute || item.attribute_name || item.column || "",
        env_id: item.env_id ?? item.envId ?? null,
        env_name: item.env_name || item.envName || "",
        pipeline_id: item.pipeline_id ?? item.pipelineId ?? null,
        pipeline_name: item.pipeline_name || item.pipelineName || "",
        pipeline_status: item.pipeline_status || item.pipelineStatus || "",
        pipelines: Array.isArray(item.pipelines) ? item.pipelines : undefined,
        metrics: Array.isArray(item.metrics) ? item.metrics : undefined,
        type: item.type || item.datasource_type || item.datasourceType || ""
    };
}

function mapMappingsPageKpi(item = {}) {
    return {
        id: item.id,
        name: item.name || "",
        description: item.description || item.definition || item.defination || "",
        active: item.active !== false,
        priority: item.priority ? toPriority(item.priority, "") : "",
        functionId: item.functionId ?? item.function_id ?? null,
        formula: item.formula || "",
        env_id: item.env_id ?? item.envId ?? null,
        env_name: item.env_name || item.envName || "",
        kpi_store_id: item.kpi_store_id ?? item.kpiStoreId ?? null
    };
}

function dedupeById(items = []) {
    const map = new Map();
    items.forEach((item) => {
        if (item == null || item.id == null) return;
        const key = String(item.id);
        if (!map.has(key)) map.set(key, item);
    });
    return Array.from(map.values());
}

/** Union entity lists by id; later lists override overlapping fields. */
export function mergeEntityListById(...lists) {
    const map = new Map();
    lists.forEach((list) => {
        (Array.isArray(list) ? list : []).forEach((item) => {
            if (item == null || item.id == null) return;
            const key = String(item.id);
            map.set(key, { ...(map.get(key) || {}), ...item });
        });
    });
    return Array.from(map.values());
}

/** Ensure placeholder businesses exist and parents are active for mapped KPIs. */
export function finalizeMappingsHierarchy(businesses = [], functions = [], kpis = []) {
    const withBusinesses = ensureBusinessesForFunctions(businesses, functions);
    const activated = activateHierarchyForActiveKpis(withBusinesses, functions, kpis);
    return {
        businesses: activated.businesses,
        functions: activated.functions,
        kpis
    };
}

/** Ensure every function.businessId has a business row so the mapping tree can render. */
function ensureBusinessesForFunctions(businesses = [], functions = []) {
    const byId = new Map(businesses.map((b) => [String(b.id), b]));
    const next = [...businesses];
    functions.forEach((f) => {
        if (f.businessId == null) return;
        const key = String(f.businessId);
        if (byId.has(key)) return;
        const placeholder = {
            id: f.businessId,
            name: `Business ${f.businessId}`,
            description: "",
            active: true,
            priority: ""
        };
        byId.set(key, placeholder);
        next.push(placeholder);
    });
    return next;
}

/** Activate parents when child KPIs are active (API may return inactive function + active KPIs). */
function activateHierarchyForActiveKpis(businesses = [], functions = [], kpis = []) {
    const activeFnIds = new Set(
        kpis.filter((k) => k.active !== false).map((k) => String(k.functionId))
    );
    const nextFunctions = functions.map((f) => (
        activeFnIds.has(String(f.id)) ? { ...f, active: true } : f
    ));
    const activeBizIds = new Set(
        nextFunctions
            .filter((f) => f.active !== false || activeFnIds.has(String(f.id)))
            .map((f) => String(f.businessId))
    );
    const nextBusinesses = businesses.map((b) => (
        activeBizIds.has(String(b.id)) ? { ...b, active: true } : b
    ));
    return { businesses: nextBusinesses, functions: nextFunctions };
}

function normalizePageMetrics(metrics) {
    if (!metrics || typeof metrics !== "object") return null;
    const totalActiveKpis = Number(metrics.totalActiveKpis) || 0;
    const mappedCount = Number(metrics.mappedCount) || 0;
    const mappedPercentRaw = Number(metrics.mappedPercent);
    return {
        autoCount: Number(metrics.autoCount) || 0,
        manualCount: Number(metrics.manualCount) || 0,
        mappedCount,
        mappedPercent: Number.isFinite(mappedPercentRaw)
            ? Math.round(mappedPercentRaw)
            : (totalActiveKpis ? Math.round((mappedCount / totalActiveKpis) * 100) : 0),
        totalActiveKpis,
        totalMappings: Number(metrics.totalMappings) || 0,
        unmappedCount: Number(metrics.unmappedCount) || 0
    };
}

export function normalizeConfigureMappingsPageResponse(apiResponse) {
    const raw = extractConfigureMappingsPageData(apiResponse);
    const assetsFromMappings = [];
    const seenAssetIds = new Set();

    const mappings = raw.mappings.map((item = {}) => {
        const nestedAsset = item.asset && typeof item.asset === "object" ? item.asset : null;
        const assetId = item.assetId ?? item.asset_id ?? (nestedAsset && nestedAsset.id) ?? null;
        if (nestedAsset && nestedAsset.id != null && !seenAssetIds.has(String(nestedAsset.id))) {
            seenAssetIds.add(String(nestedAsset.id));
            assetsFromMappings.push(nestedAsset);
        }
        return {
            id: item.id ?? item.mapping_id,
            kpiId: item.kpiId ?? item.kpi_id ?? item.kpi?.id ?? null,
            assetId,
            modelId: item.modelId ?? item.model_id ?? item.model?.id ?? null,
            status: item.status,
            enabled: item.enabled !== false && item.enable !== false
        };
    });

    const assets = [
        ...raw.assets.map(mapMappingsPageAsset),
        ...assetsFromMappings.map(mapMappingsPageAsset)
    ];

    const dedupedAssets = [];
    const assetIds = new Set();
    assets.forEach((asset) => {
        if (asset.id == null || assetIds.has(String(asset.id))) return;
        assetIds.add(String(asset.id));
        dedupedAssets.push(asset);
    });

    let businesses = raw.business.map((item) => mapCatalogueEntity(item));
    let functions = raw.functions.map((item) =>
        mapCatalogueEntity(item, {
            businessId: item.businessId ?? item.business_id
        })
    );
    // API returns one row per env/store for the same catalogue KPI id — keep one for joins
    let kpis = dedupeById(raw.kpis.map(mapMappingsPageKpi));
    const models = raw.models.map((item) =>
        mapCatalogueEntity(item, {
            functionId: item.functionId ?? item.function_id,
            kind: item.kind || ""
        })
    );

    businesses = ensureBusinessesForFunctions(businesses, functions);
    ({ businesses, functions } = activateHierarchyForActiveKpis(businesses, functions, kpis));

    return {
        assets: dedupedAssets,
        mappings,
        businesses,
        functions,
        kpis,
        models,
        metrics: normalizePageMetrics(raw.metrics),
        filter_options: raw.filter_options,
        filters: raw.filters
    };
}

export function isConfigureMappingsPageSuccess(apiResponse) {
    if (!apiResponse || typeof apiResponse !== "object") {
        return false;
    }
    if (Number(apiResponse.code) === 200 || Number(apiResponse.status_code) === 200) {
        return true;
    }
    const page = normalizeConfigureMappingsPageResponse(apiResponse);
    return (
        page.assets.length > 0
        || page.mappings.length > 0
        || page.kpis.length > 0
        || page.businesses.length > 0
    );
}
